import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, Wrench, ArrowRight, X } from 'lucide-react';
import type { ValidationIssue, ValidationReport } from '../../utils/surveyValidation';
import {
  hasBlockingErrors,
  requiresTypedConfirmation,
  autoFixableFixes,
} from '../../utils/surveyValidation';

interface SurveyValidationModalProps {
  report: ValidationReport;
  /** True when only warnings remain (no blocking errors) — a "Save anyway" override is allowed. */
  canOverride: boolean;
  busy?: boolean;
  /** Apply every auto-fixable repair server-side, then save. */
  onFixAll: () => void;
  /** Apply a single issue's auto-fix (dead-pointer removal). */
  onAutoFix?: (issue: ValidationIssue) => void;
  /** Close the modal and focus the named question in the editor. */
  onGoToQuestion: (questionId: string) => void;
  /** Save past warnings (only when there are no blocking errors). */
  onSaveAnyway: () => void;
  onCancel: () => void;
}

const CONFIRM_WORD = 'CONFIRM';

const IssueRow: React.FC<{
  issue: ValidationIssue;
  busy: boolean;
  onAutoFix?: (issue: ValidationIssue) => void;
  onGoToQuestion: (questionId: string) => void;
}> = ({ issue, busy, onAutoFix, onGoToQuestion }) => {
  const isError = issue.severity === 'error';
  const Icon = isError ? AlertCircle : AlertTriangle;
  return (
    <li
      data-testid={`issue-${issue.code}`}
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${
        isError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
      }`}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${isError ? 'text-red-600' : 'text-amber-600'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${isError ? 'text-red-800' : 'text-amber-800'}`}>{issue.message}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {issue.auto_fixable && onAutoFix && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAutoFix(issue)}
              data-testid={`autofix-${issue.code}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#07E5D1] hover:text-[#06C9B8] disabled:text-gray-400"
            >
              <Wrench className="w-3 h-3" />
              Auto-fix
            </button>
          )}
          {issue.question_id && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onGoToQuestion(issue.question_id as string)}
              data-testid={`goto-${issue.question_id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-400"
            >
              Go to question
              <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

const SurveyValidationModal: React.FC<SurveyValidationModalProps> = ({
  report,
  canOverride,
  busy = false,
  onFixAll,
  onAutoFix,
  onGoToQuestion,
  onSaveAnyway,
  onCancel,
}) => {
  const [confirmText, setConfirmText] = useState('');

  const errors = report.errors ?? [];
  const warnings = report.warnings ?? [];
  if (errors.length === 0 && warnings.length === 0) return null;

  const blocked = hasBlockingErrors(report);
  const needsTyped = requiresTypedConfirmation(report);
  const typedOk = !needsTyped || confirmText.trim() === CONFIRM_WORD;
  const hasAutoFixes = autoFixableFixes(report).length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Survey validation issues"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {blocked ? 'Fix these issues before saving' : 'Review before saving'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {blocked
                ? 'Your survey has broken references that would break it for respondents.'
                : 'These changes are allowed, but please confirm you intend them.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {errors.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-red-600 mb-2">
                Errors ({errors.length})
              </h3>
              <ul className="space-y-2">
                {errors.map((issue, i) => (
                  <IssueRow
                    key={`e-${i}`}
                    issue={issue}
                    busy={busy}
                    onAutoFix={onAutoFix}
                    onGoToQuestion={onGoToQuestion}
                  />
                ))}
              </ul>
            </section>
          )}

          {warnings.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-2">
                Warnings ({warnings.length})
              </h3>
              <ul className="space-y-2">
                {warnings.map((issue, i) => (
                  <IssueRow
                    key={`w-${i}`}
                    issue={issue}
                    busy={busy}
                    onAutoFix={onAutoFix}
                    onGoToQuestion={onGoToQuestion}
                  />
                ))}
              </ul>
            </section>
          )}

          {needsTyped && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
              <label className="block text-sm text-amber-800 mb-2">
                This survey already has responses. Type{' '}
                <span className="font-semibold">{CONFIRM_WORD}</span> to proceed.
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                aria-label="Type CONFIRM to proceed"
                className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:text-gray-400"
          >
            Cancel
          </button>

          {hasAutoFixes && (
            <button
              type="button"
              onClick={onFixAll}
              disabled={busy || !typedOk}
              data-testid="fix-all-save"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#07E5D1] hover:bg-[#06D4C2] disabled:bg-gray-200 disabled:text-gray-400 shadow-sm"
            >
              {busy ? 'Working…' : 'Fix all & Save'}
            </button>
          )}

          {!blocked && canOverride && (
            <button
              type="button"
              onClick={onSaveAnyway}
              disabled={busy || !typedOk}
              data-testid="save-anyway"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 disabled:bg-gray-200 disabled:text-gray-400 shadow-sm"
            >
              Save anyway
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyValidationModal;
